import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, X } from "lucide-react";
import { AnnouncementBar } from "../components/AnnouncementBar";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { Eyebrow, SerifH } from "../components/shared";
import { useCart } from "../context/CartContext";
import { trackEvent } from "../lib/analytics";
import { cartLinesToItems, CURRENCY } from "../lib/catalog";

const API = process.env.REACT_APP_BACKEND_URL;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const cart = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/api/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            // Fire GA4 + Meta purchase exactly once per session.
            const purchaseKey = `yaba_purchase_tracked:${sessionId}`;
            if (!window.sessionStorage.getItem(purchaseKey)) {
              try {
                trackEvent("purchase", {
                  transaction_id: sessionId,
                  currency: CURRENCY,
                  value: cart.total,
                  tax: 0,
                  shipping: cart.shipping,
                  coupon: cart.promoCode || undefined,
                  items: cartLinesToItems(cart.items),
                });
                window.sessionStorage.setItem(purchaseKey, "1");
              } catch { /* ignore */ }
            }
            cart.clear();
            cleared.current = true;
          }
          return;
        }
        if (data.status === "failed" || data.payment_status === "failed") { setStatus("failed"); return; }
        tries += 1;
        if (tries >= 30) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch { setStatus("error"); }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="bg-[#F4EDE2] text-[#0C111C] min-h-screen" data-testid="payment-success-page">
      <AnnouncementBar /><Nav />
      <section className="mx-auto max-w-[720px] px-6 py-24 md:py-32 text-center">
        {status === "polling" && (
          <div data-testid="payment-polling">
            <Loader2 size={40} className="mx-auto text-[#C8A96A] animate-spin" />
            <SerifH className="mt-6" size="clamp(28px, 3vw, 44px)">Confirming your payment…</SerifH>
            <p className="mt-4 text-[#3A3A3E]" style={{ fontFamily: "Inter, sans-serif" }}>
              Hold tight — Stripe is finalising things. This usually takes a second or two.
            </p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="payment-paid">
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#C8A96A] text-[#0C182E]">
              <Check size={28} strokeWidth={2} />
            </span>
            <Eyebrow className="mt-8 block">Order confirmed</Eyebrow>
            <SerifH className="mt-4" size="clamp(36px, 4.6vw, 68px)">Shukran.</SerifH>
            <p className="mt-6 text-lg text-[#3A3A3E]" style={{ fontFamily: "Inter, sans-serif" }}>
              Your jellaba is being folded onto a rigid board in Casablanca. You'll get a
              tracking link within 24 hours, and the box will slide through your letterbox
              in 2–4 business days.
            </p>
            <Link to="/collection" data-testid="payment-keep-shopping"
              className="mt-10 inline-flex items-center gap-2 bg-[#0C182E] text-[#F4EDE2] px-8 h-14 text-sm tracking-[0.22em] uppercase hover:bg-[#0C111C]"
              style={{ fontFamily: "Inter, sans-serif" }}>Keep shopping →</Link>
          </div>
        )}
        {["failed", "timeout", "error"].includes(status) && (
          <div data-testid="payment-failed">
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#B95841]/15 text-[#B95841]">
              <X size={28} strokeWidth={2} />
            </span>
            <SerifH className="mt-8" size="clamp(28px, 3vw, 44px)">Something didn't go through.</SerifH>
            <p className="mt-4 text-[#3A3A3E]" style={{ fontFamily: "Inter, sans-serif" }}>
              No card was charged. Please try again or reach out — we're around.
            </p>
            <div className="mt-8 flex justify-center gap-3">
              <Link to="/cart"
                className="bg-[#0C182E] text-[#F4EDE2] px-6 h-12 inline-flex items-center text-sm tracking-[0.22em] uppercase"
                style={{ fontFamily: "Inter, sans-serif" }}>Back to bag</Link>
              <Link to="/contact"
                className="border border-[#0C182E] text-[#0C182E] px-6 h-12 inline-flex items-center text-sm tracking-[0.22em] uppercase"
                style={{ fontFamily: "Inter, sans-serif" }}>Contact us</Link>
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="bg-[#F4EDE2] text-[#0C111C] min-h-screen" data-testid="payment-cancel-page">
      <AnnouncementBar /><Nav />
      <section className="mx-auto max-w-[720px] px-6 py-24 md:py-32 text-center">
        <Eyebrow>Payment cancelled</Eyebrow>
        <SerifH className="mt-4" size="clamp(30px, 3.4vw, 52px)">No worries — your bag is still here.</SerifH>
        <p className="mt-6 text-[#3A3A3E]" style={{ fontFamily: "Inter, sans-serif" }}>
          We didn't charge anything. Your items are waiting exactly where you left them.
        </p>
        <Link to="/cart" data-testid="payment-cancel-back"
          className="mt-8 inline-flex items-center gap-2 bg-[#0C182E] text-[#F4EDE2] px-8 h-14 text-sm tracking-[0.22em] uppercase hover:bg-[#0C111C]"
          style={{ fontFamily: "Inter, sans-serif" }}>Back to bag →</Link>
      </section>
      <Footer />
    </div>
  );
}
